import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MaskLine, FadeUp, ImageReveal } from "./Reveal";
import { IMAGES } from "../config/site";

const EASE = [0.22, 1, 0.36, 1];

const FILM_PAIRS = [
  { frames: [IMAGES.storyBeans, IMAGES.storyBeans2], caption: "The bean", cls: "md:col-span-8 md:row-span-2 aspect-[16/10]" },
  { frames: [IMAGES.storyFilter, IMAGES.storyFilter2], caption: "The ritual", cls: "md:col-span-4 aspect-square" },
  { frames: [IMAGES.storyBrewed, IMAGES.storyBrewed2], caption: "The brew", cls: "md:col-span-4 md:row-span-2 aspect-[4/5] md:aspect-auto" },
  { frames: [IMAGES.storyEnjoying, IMAGES.storyEnjoying2], caption: "The moment", cls: "md:col-span-8 aspect-[16/9]" },
];

const FilmImg = ({ frames, alt, tick, offset }) => (
  <div className="relative h-full w-full">
    {frames.map((src, i) => (
      <motion.img
        key={src}
        src={src}
        alt={alt}
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover"
        initial={false}
        animate={{ opacity: (tick + offset) % 2 === i ? 1 : 0 }}
        transition={{ duration: 2.6, ease: "easeInOut" }}
      />
    ))}
  </div>
);

const BrandStory = () => {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 5000);
    return () => clearInterval(id);
  }, []);

  return (
    <section id="story" data-testid="brand-story-section" className="relative py-24 md:py-32">
      <div className="mx-auto grid max-w-7xl gap-16 px-5 md:px-10 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-32">
            <FadeUp>
              <p className="mb-5 text-[11px] uppercase tracking-widest2 text-gold">Our Story</p>
            </FadeUp>
            <h2 className="font-tamil text-4xl leading-[1.25] text-cream sm:text-5xl">
              <MaskLine delay={0.05}>ஒரு Coffee...</MaskLine>
              <MaskLine delay={0.18}>ஒரு நினைவு...</MaskLine>
              <MaskLine delay={0.31} className="text-gold">
                ஒரு கதை.
              </MaskLine>
            </h2>
            <FadeUp delay={0.25} className="mt-8 space-y-5 text-base leading-relaxed text-cream/70">
              <p>
                Every South Indian morning begins the same way — the slow drip of the filter, the
                sound of milk being poured from a height, and that first warm cup passed from hand
                to hand.
              </p>
              <p>
                TMV Coffee was born from those mornings. From homes where coffee was never just a
                drink — it was the pause between two conversations, the welcome after a long day,
                the thing grandparents and grandchildren shared without needing words.
              </p>
              <p className="text-cream/85">
                We roast in small batches, blend with care, and send our coffee and tea to homes,
                cafés and shops — so that wherever you are, the first sip feels like coming home.
              </p>
            </FadeUp>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-12 lg:col-span-7">
          {FILM_PAIRS.map((img, i) => (
            <div key={i} className={`group relative ${img.cls}`}>
              <motion.div
                className="h-full w-full overflow-hidden rounded-sm"
                initial={{ clipPath: "inset(10% 6% 10% 6%)", opacity: 0 }}
                whileInView={{ clipPath: "inset(0% 0% 0% 0%)", opacity: 1 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 1.1, ease: EASE }}
              >
                <FilmImg frames={img.frames} alt={img.caption} tick={tick} offset={i} />
                <div className="absolute inset-0 bg-gradient-to-t from-espresso/60 via-transparent to-transparent opacity-0 transition-opacity duration-700 group-hover:opacity-100" />
                <span className="absolute bottom-4 left-4 translate-y-2 text-xs uppercase tracking-widest2 text-cream opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                  {img.caption}
                </span>
              </motion.div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BrandStory;
